import dayjs from 'dayjs'

export interface AssistantDataSource {
	tools: string[]
	dateFrom?: string
	dateTo?: string
	timezone?: string
}

export interface ToolCallLike {
	id?: string
	name: string
	status: 'running' | 'done' | 'error'
	result?: string
	args?: Record<string, unknown>
}

const TOOL_DISPLAY_NAMES: Record<string, string> = {
	get_store_ranking: 'Store ranking',
	get_product_ranking: 'Product ranking',
	get_cell_performance: 'Cell performance',
	get_row_performance: 'Row performance',
	get_product_affinity: 'Product affinity',
	get_affinity_placement: 'Affinity placement',
	prepare_promotion_draft: 'Promotion draft',
	get_stockouts: 'Stockouts',
	get_planogram_layout: 'Planogram layout',
	suggest_planogram_swaps: 'Planogram swaps',
	generate_planogram_layout: 'Planogram generator',
	apply_planogram_draft: 'Planogram draft',
	get_transaction_details: 'Transactions',
	search_products: 'Product search',
	search_stores: 'Store search',
	get_sales_summary: 'Sales summary',
	get_restock_status: 'Restock status',
	get_inventory_levels: 'Inventory',
	get_cabinet_slot_inventory: 'Slot inventory',
	get_ops_digest: 'Ops digest',
	get_period_comparison: 'Period comparison',
	get_restock_priorities: 'Restock priorities',
	get_store_visit_plan: 'Store visit plan',
	get_pending_planogram_review: 'Pending planogram',
	get_vision_risk_summary: 'Vision risks',
	get_payment_failures_summary: 'Payment failures',
	get_product_placement_recommendations: 'Product placement',
	get_store_cabinet_comparison: 'Cabinet comparison',
	get_store_pair_placement_map: 'Store pair map',
	get_promotion_performance: 'Promotion performance',
	suggest_promotions: 'Promotion suggestions',
	get_inventory_status: 'Inventory',
	search_help_articles: 'Help articles',
	get_help_article: 'Help article',
	navigate_to_filtered_page: 'Navigation',
}

const DATE_ARG_KEYS = [
	'from_date',
	'to_date',
	'from',
	'to',
	'start_date',
	'end_date',
	'date_from',
	'date_to',
	'since',
	'until',
] as const

const FROM_KEYS = new Set(['from_date', 'from', 'start_date', 'date_from', 'since'])
const TO_KEYS = new Set(['to_date', 'to', 'end_date', 'date_to', 'until'])

export function getToolDisplayName(name: string): string {
	return TOOL_DISPLAY_NAMES[name] ?? name.replace(/_/g, ' ')
}

function normalizeDateValue(value: unknown): string | undefined {
	if (value == null) return undefined
	const text = String(value).trim()
	if (!text) return undefined
	if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10)
	return text
}

function extractDateRangeFromArgs(args: Record<string, unknown>): { dateFrom?: string; dateTo?: string } {
	let dateFrom: string | undefined
	let dateTo: string | undefined

	for (const key of DATE_ARG_KEYS) {
		if (!(key in args)) continue
		const normalized = normalizeDateValue(args[key])
		if (!normalized) continue
		if (FROM_KEYS.has(key)) dateFrom = dateFrom ?? normalized
		if (TO_KEYS.has(key)) dateTo = dateTo ?? normalized
	}

	return { dateFrom, dateTo }
}

function extractDateRangeFromResult(text: string): { dateFrom?: string; dateTo?: string } {
	let dateFrom: string | undefined
	let dateTo: string | undefined

	const isoRange = text.match(
		/(?:from|start|since)["\s:=]+(\d{4}-\d{2}-\d{2}).{0,40}(?:to|end|until)["\s:=]+(\d{4}-\d{2}-\d{2})/i
	)
	if (isoRange) {
		return { dateFrom: isoRange[1], dateTo: isoRange[2] }
	}

	for (const match of text.matchAll(/(\d{4}-\d{2}-\d{2})/g)) {
		if (!dateFrom) dateFrom = match[1]
		else if (!dateTo && match[1] !== dateFrom) dateTo = match[1]
	}

	return { dateFrom, dateTo }
}

export function extractDataSourceFromToolCalls(toolCalls: ToolCallLike[]): AssistantDataSource | undefined {
	const tools: string[] = []
	let dateFrom: string | undefined
	let dateTo: string | undefined
	let timezone: string | undefined

	for (const tc of toolCalls) {
		if (tc.status === 'error') continue
		if (tc.name === 'navigate_to_filtered_page') continue

		const label = getToolDisplayName(tc.name)
		if (!tools.includes(label)) tools.push(label)

		if (tc.args) {
			const fromArgs = extractDateRangeFromArgs(tc.args)
			dateFrom = dateFrom ?? fromArgs.dateFrom
			dateTo = dateTo ?? fromArgs.dateTo
			if (!timezone && typeof tc.args.timezone === 'string' && tc.args.timezone.trim()) {
				timezone = tc.args.timezone.trim()
			}
		}

		if (tc.result) {
			const fromResult = extractDateRangeFromResult(tc.result)
			dateFrom = dateFrom ?? fromResult.dateFrom
			dateTo = dateTo ?? fromResult.dateTo
			try {
				const parsed = JSON.parse(tc.result) as Record<string, unknown>
				if (!timezone && typeof parsed.timezone === 'string' && parsed.timezone.trim()) {
					timezone = parsed.timezone.trim()
				}
			} catch {
				// not JSON
			}
		}
	}

	if (!tools.length && !dateFrom && !dateTo && !timezone) return undefined
	return { tools, dateFrom, dateTo, timezone }
}

function formatTimezoneAbbrev(ianaTimezone: string): string {
	try {
		const parts = new Intl.DateTimeFormat('en-US', {
			timeZone: ianaTimezone,
			timeZoneName: 'short',
		}).formatToParts(new Date())
		const abbr = parts.find((part) => part.type === 'timeZoneName')?.value
		if (abbr) return abbr
	} catch {
		// ignore invalid timezone
	}
	return ianaTimezone
}

// Intl gives "GMT-2:30" for half-hour zones like NDT — normalize to
// "UTC-02:30" so the footer doesn't look like a raw offset dump.
function formatTimezoneLabel(ianaTimezone: string): string {
	const abbr = formatTimezoneAbbrev(ianaTimezone)
	const gmt = abbr.match(/^GMT([+-])(\d{1,2})(?::(\d{2}))?$/)
	if (!gmt) return abbr
	const [, sign, hours, minutes] = gmt
	return `UTC${sign}${hours.padStart(2, '0')}:${minutes ?? '00'}`
}

function formatDateRange(from: string, to: string): string {
	const a = dayjs(from)
	const b = dayjs(to)
	if (!a.isValid() || !b.isValid()) return `${from} – ${to}`
	if (a.isSame(b, 'day')) return a.format('MMM D, YYYY')
	if (a.isSame(b, 'month')) return `${a.format('MMM D')}–${b.format('D, YYYY')}`
	if (a.isSame(b, 'year')) return `${a.format('MMM D')} – ${b.format('MMM D, YYYY')}`
	return `${a.format('MMM D, YYYY')} – ${b.format('MMM D, YYYY')}`
}

export function formatDataSourceFooter(dataSource: AssistantDataSource): string {
	const parts: string[] = []
	if (dataSource.tools.length) {
		parts.push(dataSource.tools.join(', '))
	}
	if (dataSource.dateFrom && dataSource.dateTo) {
		parts.push(formatDateRange(dataSource.dateFrom, dataSource.dateTo))
	} else if (dataSource.dateFrom) {
		parts.push(`From ${dayjs(dataSource.dateFrom).format('MMM D, YYYY')}`)
	} else if (dataSource.dateTo) {
		parts.push(`Through ${dayjs(dataSource.dateTo).format('MMM D, YYYY')}`)
	}
	if (dataSource.timezone) {
		parts.push(formatTimezoneLabel(dataSource.timezone))
	}
	return parts.join(' • ')
}
